// ═══════════════════════════════════════════════════════════════════════════
// LES CATÉGORIES NÉERLANDAISES DU BLOG — /gids/categorie/<slug>
// ───────────────────────────────────────────────────────────────────────────
// POURQUOI CE FICHIER EXISTE (mesuré le 07/09/2026, avec son dénominateur).
//
// L'index /blog a été séparé par langue le 29/08/2026 : les articles
// néerlandais ont reçu leur propre porte d'entrée, /gids. La couche juste en
// dessous — les 10 pages `/blog/categorie/<tag>` — n'a PAS suivi. Résultat
// relevé sur le registre :
//
//   · 10 catégories publiées sur 10 mélangeaient les deux langues ;
//   · 68 articles néerlandais sur 68 étaient listés dans une page dont le
//     titre, la description et l'introduction sont en français ;
//   · et le bloc « Verwante gidsen » de CHAQUE article néerlandais renvoyait
//     son lecteur, en néerlandais, vers cette page française.
//
// Un lecteur flamand cliquait donc « Alle gidsen "Loon" » et atterrissait sur
// « Calcul de la paie en Belgique ». Pour Google, c'est le même signal que
// celui déjà corrigé sur l'index : il ne sait pas dans quelle langue classer
// la page, et il finit par servir la version française à une requête
// néerlandaise.
//
// ⚠️ CE QUI N'EST PAS AFFIRMÉ ICI (R2 sexies) : que ce défaut soit LA cause du
// classement de telle ou telle requête. Ce n'est pas mesuré. Ce qui est mesuré,
// c'est le défaut lui-même — 10 sur 10, 68 sur 68 — et il se répare pour
// lui-même.
//
// ───────────────────────────────────────────────────────────────────────────
// UNE SEULE LISTE, PAS DEUX. Les étiquettes néerlandaises vivaient déjà dans
// app/gids/page.js. Les recopier ici aurait fabriqué la 2ᵉ liste qui diverge —
// exactement le défaut raconté en tête de blog-registry.js (5 listes d'articles
// désynchronisées, 31 articles orphelins). /gids importe donc CE fichier.
//
// ⚠️ Les tags du registre restent FRANÇAIS ('Paie', 'RH', 'Rupture'…) : ils
// sont partagés par les deux versants et servent de clé. On ne renomme pas la
// donnée, on traduit à l'affichage — même règle que dans /gids.
//
// R1 : aucun chiffre légal dans les textes ci-dessous. Les montants vivent dans
// les articles et dans lois-belges.js.
// ═══════════════════════════════════════════════════════════════════════════
import { categoriesPubliables, articlesParTag } from './blog-registry';

/** Tag du registre (français) → étiquette néerlandaise affichée. */
export const ETIQUETTE_NL = {
  Paie: 'Loon',
  Déclarations: 'Aangiften',
  Primes: 'Premies',
  Embauche: 'Aanwerving',
  RH: 'HR',
  Rupture: 'Ontslag',
  Secteurs: 'Sectoren',
  Aides: 'Steunmaatregelen',
  Outils: 'Tools',
  Guide: 'Gids',
  Comparatif: 'Vergelijking',
};

/**
 * Tag → slug d'URL NÉERLANDAIS.
 *
 * ⚠️ Le slug se dérive de l'étiquette néerlandaise, jamais du tag français.
 * `/gids/categorie/rupture` serait une URL française sous une page
 * néerlandaise : le lecteur le voit, et le moteur aussi.
 * « categorie » est un mot néerlandais autant que français — le segment de
 * chemin, lui, n'a donc pas à changer.
 */
export const SLUG_NL = {
  Paie: 'loon',
  Déclarations: 'aangiften',
  Primes: 'premies',
  Embauche: 'aanwerving',
  RH: 'hr',
  Rupture: 'ontslag',
  Secteurs: 'sectoren',
  Aides: 'steunmaatregelen',
  Outils: 'tools',
  Guide: 'gids',
  Comparatif: 'vergelijking',
};

/**
 * Titre, description et introduction de chaque catégorie néerlandaise.
 *
 * ⚠️ ÉCRITS, PAS TRADUITS (R8 bis). Une description recopiée mot pour mot du
 * français donne une phrase qui se lit comme une traduction — et la moitié
 * néerlandophone du marché la reconnaît immédiatement. L'intention est la même
 * que côté français ; les phrases, non.
 */
export const TEXTES_NL = {
  Paie: {
    h1: 'Loonberekening in België',
    title: 'Loonberekening België: van brutoloon naar nettoloon',
    desc: 'Alle gidsen over de Belgische loonberekening: van bruto naar netto, RSZ-bijdragen, bedrijfsvoorheffing, overuren en vakantiegeld.',
    intro: "Van een brutoloon naar wat de werknemer werkelijk op zijn rekening ziet, liggen verschillende lagen regels: sociale bijdragen, bedrijfsvoorheffing, verminderingen en voordelen. Deze gidsen nemen elke stap van de berekening door, met de bijzondere gevallen die bij Belgische werkgevers het vaakst terugkomen.",
  },
  RH: {
    h1: 'HR en Belgisch arbeidsrecht',
    title: 'HR in België: arbeidsrecht, verlof en arbeidsduur',
    desc: 'HR-gidsen voor Belgische werkgevers: arbeidsduur, verlof, tijdskrediet, telewerk, arbeidsongeschiktheid, tijdelijke werkloosheid en aanvullend pensioen.',
    intro: 'Het Belgische arbeidsrecht regelt veel meer dan het loon: uurroosters, rust, verlof, afwezigheden, het einde van de werkdag en het einde van de loopbaan. Deze gidsen behandelen de situaties die een werkgever in de praktijk tegenkomt, met wat de wet oplegt en wat ze aan de overeenkomst overlaat.',
  },
  Primes: {
    h1: 'Premies en extralegale voordelen',
    title: 'Extralegale voordelen in België: gids voor werkgevers',
    desc: 'Maaltijdcheques, ecocheques, eindejaarspremie, bedrijfswagen, telewerkvergoeding en mobiliteitsbudget: voorwaarden, sociale en fiscale behandeling.',
    intro: 'Een aanzienlijk deel van het Belgische loonpakket loopt via voordelen die geheel of gedeeltelijk buiten de bijdragen blijven — op voorwaarde dat precieze regels worden nageleefd. Deze gidsen leggen per voordeel de vrijstellingsvoorwaarden uit, en wat een voordeel doet omslaan in belastbaar loon.',
  },
  Comparatif: {
    h1: 'Sociale secretariaten vergeleken',
    title: 'Sociaal secretariaat vergelijken: tarieven en alternatieven',
    desc: 'Vergelijkingen van Belgische sociale secretariaten: reële tarieven, vergeten kostenposten, de overstapprocedure en digitale alternatieven.',
    intro: 'Een sociaal secretariaat kiezen of verlaten bindt een onderneming voor meerdere jaren, en de aangekondigde tarieven verbergen vaak kosten die pas op de factuur opduiken. Deze vergelijkingen zetten de modellen naast elkaar — wat werkelijk gedekt is, de prijs, de kalenderverplichtingen — zonder commerciële kortsluiting.',
  },
  Secteurs: {
    h1: 'Loon per sector en paritair comité',
    title: "Loon per sector in België: paritaire comités en barema's",
    desc: "Loonverwerking per sector: horeca, bouw, transport, vzw's en boekhoudkantoren. Paritaire comités, sectorale barema's en verplichtingen per sector.",
    intro: 'In België bepaalt de activiteit het paritair comité, en dat comité legt barema’s, premies en verplichtingen op die elders niet bestaan. Deze gidsen behandelen de sectoren waar de regels het sterkst afwijken van het algemene stelsel.',
  },
  Déclarations: {
    h1: 'Sociale en fiscale aangiften',
    title: 'Dimona, DmfA en Belcotax: gids bij de Belgische aangiften',
    desc: 'Gidsen bij de verplichte Belgische aangiften: Dimona, kwartaalaangifte DmfA, fiscale fiches Belcotax en sociale balans. Termijnen en veelgemaakte fouten.',
    intro: 'Elke indienstneming, elk kwartaal en elk boekjaar zet aangiften in gang waarvan de termijnen niet in te halen zijn. Deze gidsen beschrijven per aangifte de procedure, wat ze bevat, en hoe u een aangifte rechtzet die met een fout is vertrokken.',
  },
  Outils: {
    h1: 'Loonsoftware en tools',
    title: 'Loonsoftware in België: vergelijking en keuzecriteria',
    desc: 'Hoe kiest u Belgische loonsoftware: criteria, onmisbare functies, multiclient-oplossingen voor boekhoudkantoren en toegang voor de externe boekhouder.',
    intro: 'Belgische loonsoftware moet de paritaire comités volgen, de aangiften produceren en de boekhoudkundige exports leveren — alle drie, anders maakt ze werk in plaats van het weg te nemen. Deze gidsen zetten uiteen waar u naar kijkt vóór u zich vastlegt.',
  },
  Embauche: {
    h1: 'Aanwerven in België',
    title: 'Aanwerven in België: contract, Dimona en eerste plichten',
    desc: 'Gidsen om aan te werven in België: eerste werknemer, arbeidsovereenkomst, Dimona, studenten, flexi-jobs, deeltijds werk en arbeidsongevallenverzekering.',
    intro: 'De eerste aanwerving zet een reeks verplichtingen in gang waarvan er verschillende vervuld moeten zijn nog vóór de werknemer begint. Deze gidsen volgen de werkelijke volgorde van de stappen, van de keuze van het contract tot de aangiften op de dag van de indiensttreding.',
  },
  Aides: {
    h1: 'Tewerkstellingssteun en bijdrageverminderingen',
    title: 'Tewerkstellingssteun België: RSZ-verminderingen en premies',
    desc: 'Aanwervingssteun en verminderingen van bijdragen: eerste aanwervingen, doelgroepverminderingen, Activa en Brusselse gewestpremies. Voorwaarden en stappen.',
    intro: 'Aanwerven opent vaak recht op verminderingen van bijdragen of op gewestelijke premies, maar die gelden bijna nooit automatisch: ze moeten worden aangevraagd, binnen de termijn, met de juiste stukken. Deze gidsen zetten de voorwaarden op een rij, en wat u verliest door ze niet op te vragen.',
  },
  Rupture: {
    h1: 'Einde contract en ontslag',
    title: 'Ontslag in België: opzegtermijn en verplichte documenten',
    desc: 'Einde van de arbeidsovereenkomst in België: opzegtermijn, ontslag, beëindiging in onderling akkoord, verplichte uitdiensttredingsdocumenten en C4.',
    intro: 'Een einde van contract wordt voorbereid: de opzegtermijn hangt af van de anciënniteit en van de wijze van beëindiging, en verschillende documenten moeten aan de werknemer worden bezorgd — zoniet blijft de werkgever nog lang na het vertrek blootgesteld. Deze gidsen nemen elke weg van beëindiging door en wat ze oplegt.',
  },
};

/** Étiquette néerlandaise d'un tag ; le tag lui-même en dernier recours. */
export const etiquetteNl = (tag) => ETIQUETTE_NL[tag] || tag;

/**
 * Les catégories qui MÉRITENT une page néerlandaise : au moins
 * MIN_ARTICLES_CATEGORIE articles NÉERLANDAIS, et un texte écrit ici.
 * Une catégorie sans texte n'est pas publiée plutôt que d'afficher un gabarit
 * vide — même règle que côté français.
 */
export function categoriesNlPubliables() {
  return categoriesPubliables('nl')
    .filter((c) => TEXTES_NL[c.tag] && SLUG_NL[c.tag])
    .map((c) => ({ tag: c.tag, slug: SLUG_NL[c.tag], nb: c.nb, etiquette: etiquetteNl(c.tag) }));
}

/** 'ontslag' → 'Rupture' (ou null). Réciproque de SLUG_NL. */
export function slugNlVersTag(slug) {
  return Object.keys(SLUG_NL).find((t) => SLUG_NL[t] === slug) || null;
}

/** Articles néerlandais d'un tag, les plus récents d'abord. */
export const articlesNlParTag = (tag) => articlesParTag(tag, 'nl');
